/**
 * 공지사항 읽음 처리 localStorage 유틸리티
 */

const STORAGE_KEY = 'readNoticeIds';
const LAST_SEEN_COMBINATION_KEY = 'lastSeenNoticeCombination';
const DONT_SHOW_TODAY_KEY = 'dontShowNoticeToday';

/**
 * 읽은 공지사항 ID 목록을 가져옵니다
 * @returns {Array<number>} 읽은 공지사항 ID 배열
 */
export const getReadNoticeIds = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error('읽은 공지사항 조회 실패:', error);
        return [];
    }
};

/**
 * 공지사항 ID를 읽은 목록에 추가합니다
 * @param {number} noticeId - 공지사항 ID
 */
export const addReadNoticeId = (noticeId) => {
    try {
        const readIds = getReadNoticeIds();
        if (!readIds.includes(noticeId)) {
            readIds.push(noticeId);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(readIds));
        }
    } catch (error) {
        console.error('읽은 공지사항 저장 실패:', error);
    }
};

/**
 * 여러 공지사항 ID를 읽은 목록에 추가합니다
 * @param {Array<number>} noticeIds - 공지사항 ID 배열
 */
export const addReadNoticeIds = (noticeIds) => {
    try {
        const readIds = getReadNoticeIds();
        const newIds = noticeIds.filter(id => !readIds.includes(id));
        if (newIds.length > 0) {
            const updatedIds = [...readIds, ...newIds];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedIds));
        }
    } catch (error) {
        console.error('읽은 공지사항 저장 실패:', error);
    }
};

/**
 * 공지사항이 읽었는지 확인합니다
 * @param {number} noticeId - 공지사항 ID
 * @returns {boolean} 읽었으면 true, 아니면 false
 */
export const isNoticeRead = (noticeId) => {
    const readIds = getReadNoticeIds();
    return readIds.includes(noticeId);
};

/**
 * 읽은 공지사항 목록을 초기화합니다
 */
export const clearReadNoticeIds = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('읽은 공지사항 초기화 실패:', error);
    }
};

/**
 * 오늘 하루 안보기 설정 (오늘 날짜 저장)
 */
export const setDontShowToday = () => {
    try {
        const today = new Date().toDateString(); // YYYY-MM-DD 형식
        localStorage.setItem(DONT_SHOW_TODAY_KEY, today);
    } catch (error) {
        console.error('오늘 하루 안보기 설정 실패:', error);
    }
};

/**
 * 오늘 하루 안보기 여부 확인
 * @returns {boolean} 오늘 하루 안보기가 설정되어 있으면 true
 */
export const isDontShowToday = () => {
    try {
        const storedDate = localStorage.getItem(DONT_SHOW_TODAY_KEY);
        if (!storedDate) return false;
        
        const today = new Date().toDateString();
        return storedDate === today;
    } catch (error) {
        console.error('오늘 하루 안보기 확인 실패:', error);
        return false;
    }
};

/**
 * 마지막으로 확인한 공지사항 ID 조합을 저장합니다
 * @param {Array<number>} noticeIds - 공지사항 ID 배열
 */
export const setLastSeenNoticeCombination = (noticeIds) => {
    try {
        const sortedIds = [...noticeIds].sort((a, b) => a - b);
        localStorage.setItem(LAST_SEEN_COMBINATION_KEY, JSON.stringify(sortedIds));
    } catch (error) {
        console.error('마지막 공지 조합 저장 실패:', error);
    }
};

/**
 * 현재 공지사항 ID 조합이 마지막으로 확인한 조합과 다른지 확인합니다
 * @param {Array<number>} currentIds - 현재 공지사항 ID 배열
 * @returns {boolean} 다르면 true, 같으면 false
 */
export const isNoticeCombinationChanged = (currentIds) => {
    try {
        const stored = localStorage.getItem(LAST_SEEN_COMBINATION_KEY);
        if (!stored) return true;
        
        const lastIds = JSON.parse(stored);
        if (!Array.isArray(lastIds)) return true;
        
        if (lastIds.length !== currentIds.length) return true;
        
        const sortedCurrent = [...currentIds].sort((a, b) => a - b);
        return !sortedCurrent.every((id, index) => id === lastIds[index]);
    } catch (error) {
        console.error('공지 조합 비교 실패:', error);
        return true;
    }
};
